import React, { createContext, useContext, useState, useEffect } from 'react';
import { adminAPI } from '../services/api';

interface AuthContextType {
  subdomain: string | null;
  setSubdomain: (subdomain: string) => void;
  clearSubdomain: () => void;
  isConfigured: boolean;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  adminEmail: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subdomain, setSubdomainState] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [adminEmail, setAdminEmail] = useState<string | null>(null);

  useEffect(() => {
    const savedSubdomain = localStorage.getItem('tenantSubdomain');
    if (savedSubdomain) {
      setSubdomainState(savedSubdomain);
    }

    const token = localStorage.getItem('adminToken');
    if (token) {
      adminAPI
        .verify(token)
        .then((data) => {
          setIsAuthenticated(true);
          setAdminEmail(data.email);
        })
        .catch(() => {
          localStorage.removeItem('adminToken');
        })
        .finally(() => {
          setIsAuthLoading(false);
        });
    } else {
      setIsAuthLoading(false);
    }
  }, []);

  const setSubdomain = (subdomain: string) => {
    localStorage.setItem('tenantSubdomain', subdomain);
    setSubdomainState(subdomain);
  };

  const clearSubdomain = () => {
    localStorage.removeItem('tenantSubdomain');
    setSubdomainState(null);
  };

  const login = async (email: string, password: string) => {
    const data = await adminAPI.login(email, password);
    localStorage.setItem('adminToken', data.token);
    setIsAuthenticated(true);
    setAdminEmail(data.email);
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setAdminEmail(null);
  };

  return (
    <AuthContext.Provider
      value={{
        subdomain,
        setSubdomain,
        clearSubdomain,
        isConfigured: !!subdomain,
        isAuthenticated,
        isAuthLoading,
        adminEmail,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
